import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contextSourcesApi } from '@/api/contextSources'
import { downloadBlob } from '@/lib/downloadBlob'
import { useTenantKey } from './useTenantKey'
import type { UploadContextRepositoriesRequest, UploadContextWikiPagesRequest, PaginationParams, ContextSourceFilters } from '@/types'

export function useContextSources(
  projectId: string, pagination?: PaginationParams, filters?: ContextSourceFilters, options?: { enabled?: boolean }
) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'context-sources', params),
    queryFn: () => contextSourcesApi.getAll(projectId, params),
    enabled: !!projectId && (options?.enabled ?? true),
  })
}

export function useUploadContextFiles(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: FormData) => contextSourcesApi.uploadFiles(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
    },
  })
}

export function useUploadContextRepositories(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: UploadContextRepositoriesRequest) =>
      contextSourcesApi.uploadRepositories(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
    },
  })
}

export function useUploadContextWikiPages(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: UploadContextWikiPagesRequest) =>
      contextSourcesApi.uploadWikiPages(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
    },
  })
}

export function useDeleteContextSource(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) => contextSourcesApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
    },
  })
}

export function useDownloadContextSource(projectId: string) {
  return useMutation({
    mutationFn: (id: string) => contextSourcesApi.download(projectId, id),
    onSuccess: ({ blob, fileName }) => {
      downloadBlob(blob, fileName)
    },
  })
}

export function useContextSourceContent(projectId: string, sourceId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'context-sources', sourceId!, 'content'),
    queryFn: () => contextSourcesApi.getContent(projectId, sourceId!),
    enabled: !!projectId && !!sourceId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateContextSourceContent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      contextSourcesApi.updateContent(projectId, id, content),
    onSuccess: (_data, { id }) => {
      queryClient.removeQueries({ queryKey: tenantKey('projects', projectId, 'context-sources', id, 'content') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'context-sources') })
    },
  })
}

export function useContextSourceBranches(projectId: string, contextSourceId: string, search: string, enabled: boolean) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'context-sources', contextSourceId, 'branches', search),
    queryFn: () => contextSourcesApi.listBranches(projectId, contextSourceId, search || undefined),
    enabled: enabled && !!projectId && !!contextSourceId,
    staleTime: 60 * 1000,
  })
}
